import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPendingActions, type PendingActionItem } from '../../api/pendingActions';
import { onPendingActionsChanged } from '../pendingActions/usePendingActions';
import { notificationActionKey, type ActionableNotification } from './types';

/**
 * Hydrates the notification list against the live Pending Actions queue.
 *
 * A notification says only "this row is about entity X of source Y". Everything
 * the buttons need — action style, meta, current status — comes from the queue,
 * so it can never go stale: once the item is resolved anywhere (another approver,
 * mobile, the inbox page) the lookup misses and the row quietly degrades to
 * informational. Same mechanism keeps a CC'd non-approver from seeing buttons
 * they cannot use.
 *
 * One request per list load, only when at least one row is actionable, and only
 * while the surface is on screen. Failure leaves the map empty: the list renders
 * exactly as it does today rather than blocking on hydration.
 *
 * `truncated` says the queue holds more than one page: rows past HYDRATION_PAGE_SIZE
 * miss the lookup and degrade to informational, which is indistinguishable from
 * "already resolved". Surfaces MUST show it so a busy approver is told to open the
 * full queue instead of silently losing their inline buttons.
 */
/** One page is enough for the bell; deeper queues are handled by /pending-actions. */
const HYDRATION_PAGE_SIZE = 100;

export function useActionableIndex(notifications: ActionableNotification[], enabled: boolean) {
  const [index, setIndex] = useState<Map<string, PendingActionItem>>(new Map());
  const [truncated, setTruncated] = useState(false);
  const requestIdRef = useRef(0);

  const hasActionable = useMemo(
    () => notifications.some((n) => notificationActionKey(n) !== null),
    [notifications],
  );

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!enabled || !hasActionable) {
      setIndex((prev) => (prev.size === 0 ? prev : new Map()));
      setTruncated(false);
      return;
    }
    try {
      // No `types` filter: the bell list is mixed-source by nature.
      const result = await getPendingActions({ pageSize: HYDRATION_PAGE_SIZE });
      if (requestId !== requestIdRef.current) return;
      if (result.errors.length > 0) {
        setIndex(new Map());
        setTruncated(false);
        return;
      }
      const next = new Map<string, PendingActionItem>();
      for (const item of result.payload.items) next.set(`${item.source}:${item.id}`, item);
      setIndex(next);
      setTruncated(result.payload.totalCount > result.payload.items.length);
    } catch {
      // Degrade to today's behaviour; never block the list on hydration.
      if (requestId === requestIdRef.current) {
        setIndex(new Map());
        setTruncated(false);
      }
    }
  }, [enabled, hasActionable]);

  useEffect(() => {
    load();
  }, [load]);

  // A decision taken anywhere removes the item; re-hydrate so the buttons go.
  useEffect(() => onPendingActionsChanged(load), [load]);

  return { index, truncated, refresh: load };
}
